import { Box, Grid, Typography } from '@mui/material'
import { GetStaticProps } from 'next'
import dynamic from 'next/dynamic'
import map from 'ramda/src/map'

import { Area } from '@ecm/data/Area'
import { Article } from '@ecm/data/Article'
import { lookupState } from '@ecm/data/State'
import { capitalize } from '@ecm/data/String'
import { runEff } from '@ecm/effect'
import BlogArticles from '@ecm/ui/component/addresses/BlogArticles'
import HowItWorks from '@ecm/ui/component/addresses/HowItWorks'
import MapWithPlaceholder from '@ecm/ui/component/addresses/MapWithPlaceholder'
import StateAddressList from '@ecm/ui/component/addresses/StateAddressList'
import TopSection from '@ecm/ui/component/addresses/TopSection'
import { BreadCrumb } from '@ecm/ui/element/Breadcrumbs'
import Color from '@ecm/ui/element/Color'
import MainContainer from '@ecm/ui/element/MainContainer'
import ReadMoreToggle from '@ecm/ui/element/ReadMoreToggle'
import useSmallScreen from '@ecm/ui/hook/UseSmallScreen'
import DefaultLayout from '@ecm/ui/layout/Default'
import { SelectedAddressesProvider } from '@ecm/ui/provider/SelectedAddressesProvider'
import { VisibleAddressesProvider } from '@ecm/ui/provider/VisibleAddressesProvider'

const DynamicStickyBanner = dynamic(() => import('@ecm/ui/component/StickyBanner'))

type AreaPageProps = {
  area: Area
  articles: Article[]
  breadCrumb: BreadCrumb[]
}

export const AreaPage = ({ area, articles, breadCrumb }: AreaPageProps) => {
  const isSmallScreen = useSmallScreen()

  const articleBeforeCityName = ['a', 'e', 'i', 'o', 'u', 'y'].includes(
    area.cityDisplay[0].toLowerCase()
  )
    ? 'an'
    : 'a'

  return (
    <DefaultLayout
      hideFinalCta
      seoTitle={`${area.cityDisplay} Address | Earth Class Mail`}
      seoDescription={`Get a real street address or PO box in ${area.name} for personal privacy or to register your business. Access your postal mail even when you’re on the move.`}
      canonicalUrl={`addresses/${area.stateAbbreviation}/${area.city}`}
    >
      <Box
        sx={{
          background: `linear-gradient(180deg, ${Color.smoke.normal} 520px, rgba(255, 255, 255, 0) 750px)`,
        }}
      >
        <TopSection breadCrumb={breadCrumb} header={`Address in ${area.name}`}>
          <ReadMoreToggle color={Color.smoke.normal} maxHeight={isSmallScreen ? '300px' : 'none'}>
            <Typography variant="p3">
              <b>{`Receive mail at a real street address. Need ${articleBeforeCityName} ${area.cityDisplay} address? `}</b>
              {`Earth Class Mail gives you the ability to have a mailing address in ${area.cityDisplay} without having to pay 
              for real estate, rent ${articleBeforeCityName} ${area.cityDisplay} PO Box, or check your mailbox with its virtual 
              mailbox in ${area.cityDisplay}. We scan and forward mail in ${area.cityDisplay}. 
              Remote businesses don't need an office to establish a business, thanks to our ${area.cityDisplay} street address. 
              You can use your virtual address in ${area.cityDisplay} to register a business or on your company marketing website. 
              ${area.cityDisplay} virtual addresses can also help to keep your home address private.`}
            </Typography>
          </ReadMoreToggle>
        </TopSection>

        <MainContainer>
          <Typography variant="h4" component="h2" margin="0 0 1em">{`Get the perfect ${capitalize(
            area.cityDisplay
          )} address`}</Typography>

          <SelectedAddressesProvider>
            <VisibleAddressesProvider initialAddresses={area.addresses}>
              <Box
                bgcolor={{ md: Color.white.normal }}
                border={{ md: `1px solid ${Color.smoke.darker}` }}
                borderRadius="8px"
                overflow="hidden"
              >
                <Grid container borderRadius="8px">
                  <Grid item xs={12} md={6} lg={5} maxHeight={{ md: '600px' }}>
                    <StateAddressList allAddresses={isSmallScreen ? area.addresses : undefined} />
                  </Grid>
                  {isSmallScreen === false && (
                    <Grid item md={6} lg={7} height="600px">
                      <MapWithPlaceholder addresses={area.addresses} />
                    </Grid>
                  )}
                </Grid>
              </Box>
            </VisibleAddressesProvider>
          </SelectedAddressesProvider>
        </MainContainer>
      </Box>

      <HowItWorks />
      <Box bgcolor={Color.smoke.lightest}>
        {area.description && (
          <MainContainer
            /* eslint-disable sort-keys */
            sx={{ paddingBottom: { xs: '2em', md: '4em' }, paddingTop: { xs: '3em', md: '6em' } }}
            /* eslint-disable sort-keys */
          >
            <Typography variant="h3" component="h2" margin="0 0 1em" maxWidth="600px">
              Why get an address in {area.name}
            </Typography>
            <ReadMoreToggle
              color={Color.smoke.lightest}
              maxHeight={isSmallScreen ? '300px' : 'none'}
            >
              <Typography variant="p3" columnGap="60px" sx={{ columns: { md: 2, xs: 1 } }}>
                {area.description}
              </Typography>
            </ReadMoreToggle>
          </MainContainer>
        )}
      </Box>

      <BlogArticles articles={articles} />

      <DynamicStickyBanner trackingId="stickySignupBannerAddress" eventName="addressOfferClick" />
    </DefaultLayout>
  )
}

export const getStaticProps: GetStaticProps = async ctx => {
  const stateSlug = ctx.params?.state_slug as string
  const citySlug = ctx.params?.city_slug as string

  const area = await runEff(eff => {
    return eff.getArea(`${citySlug}-${stateSlug}`)
  })

  const stateName = lookupState(stateSlug)

  const breadCrumb: BreadCrumb[] = [
    { description: 'Addresses', href: '/addresses', position: 2 },
    { description: stateName, href: `/addresses/${stateSlug}`, position: 3 },
    { description: area.cityDisplay, href: `/addresses/${stateSlug}/${citySlug}`, position: 4 },
  ]

  const randomArticles = await runEff(async eff => {
    try {
      const articlesMetadata = await eff.getArticlesMetadataByFieldValue('shouldLink')
      const limit = 3
      const min = limit
      const max = articlesMetadata.total - min // keep the difference between the total and possible max skip at the limit level
      const skip = Math.floor(Math.random() * (max - min) + min)
      const allArticles = await eff.getRelatedArticles('shouldLink', undefined, {
        limit: limit,
        order: '-fields.publishedDate',
        skip: skip,
      })
      return allArticles
    } catch (e) {
      return []
    }
  })

  return {
    props: {
      area,
      articles: randomArticles,
      breadCrumb,
    },
  }
}

export async function getStaticPaths() {
  const areas: Area[] = await runEff(eff => {
    return eff.getAreas()
  })

  const paths = map(
    a => ({
      params: {
        city_slug: a.city,
        state_slug: a.stateAbbreviation,
      },
    }),
    areas
  )

  return {
    fallback: false,
    paths,
  }
}

export default AreaPage
